import { assocPath, is, lte, not, path, pathOr, prop, propOr, isNotNil, isNotEmpty, last } from 'ramda';
import React, { useEffect, useRef } from 'react';
import { DeleteOutlined, SlidersOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { typeIsOnFlexiget } from '../helpers/elements';
import { WIDTH_A4_WITHOUT_MARGINS } from '../helpers/widget';
import { getElementByVersion } from '../helpers/flexigets';

const TEXT_UPDATE_DEBOUNCE_TIME = 500;

const DroppedCard = ({
  id,
  type,
  handleDelete,
  values,
  setState,
  renderToolbar,
  renderToolbarOld,
  renderVariable,
  isFocus,
  widgetIsEditable,
  optionsElement,
  onChangeContentHeight,
  customColors,
  customStyles,
  defaultFont,
  orientation,
  onCreateStyle,
  isFrameConditionedElements,
  onDuplicateElement,
  isToolbarFixed,
  isLateEdit,
  addToHistory,
  updateFrameStyle,
  isUndoPreviousEditorState,
  setIsUndoPreviousEditorState,
  historyActions,
  addActionOnPrevious
}) => {
  const content = path(['contents', id], values);

  const refElement = useRef(null);
  const element = assocPath(['element', 0, 'orientation'], orientation, content);
  const clientHeight = path(['current', 'clientHeight'], refElement);

  const ElementFlexiget = getElementByVersion(prop('type', element), propOr('1.0', 'version', element)).Component;

  useEffect(() => {
    if (lte(path(['size', 'height'], element), clientHeight)) {
      onChangeContentHeight(clientHeight);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientHeight]);

  const handleDuplicate = () => onDuplicateElement(id);

  const onChangeElement = (newValue, isFirstRender, isTextUpdate) => {
    const currentTime = Date.now();
    const lastHistoryAction = last(historyActions);
    const lastSavedTime = propOr(0, 'timestamp', lastHistoryAction);
    const lastChangeWasTextUpdate = propOr(false, 'isTextUpdate', lastHistoryAction);
    const updatedValues = assocPath(['contents', id, 'element'], (is(Array, newValue) ? newValue : [newValue]), values);
    const hasHtmlContent = isNotEmpty(path(['contents', id, 'element', 0, 'html'], values));

    if (hasHtmlContent && !isFirstRender) {
      // Calculate the time difference since the last saved action
      const timeDifference = currentTime - lastSavedTime;
      const shouldUpdateLastAction = isTextUpdate && lastChangeWasTextUpdate && timeDifference < TEXT_UPDATE_DEBOUNCE_TIME;
      const newContents = prop('contents', updatedValues);
      if (shouldUpdateLastAction) {
        // Update the last historical action if conditions are met
        addActionOnPrevious(newContents);
      } else {
        // Add a new entry to the history
        addToHistory(prop('contents', values), newContents, 'onChangeElement', isTextUpdate);
      }
    }

    setState(assocPath(['values', 'contents', id, 'element'], (is(Array, newValue) ? newValue : [newValue])));
  };

  const isWithRules = isFrameConditionedElements && isNotNil(prop('rule', content));
  return (
    <>
      {widgetIsEditable && isFocus && (
        <button
          onClick={() => handleDelete(id)}
          className="btn-remove-element opacity-100 absolute z-10 right-0 -top-[23px] bg-flexi-primary-color border-none cursor-pointer px-2"
        >
          <DeleteOutlined className="text-xs text-white" />
        </button>
      )}
      {isWithRules && !isFocus && (
        <div className="absolute z-10 right-0 -top-[23px] bg-flexi-red border-none cursor-pointer px-2">
          <SlidersOutlined rotate={90} className="text-xs text-white"/>
        </div>
      )}
      <div className={classNames('flexiget relative flex', { 'outline outline-[1.5px] outline-flexi-red outline-offset-[0.5px]': isWithRules })} ref={refElement}>
        {typeIsOnFlexiget(type)(optionsElement) && (
          <ElementFlexiget
            value={prop('element', element)}
            onChange={onChangeElement}
            renderToolbar={renderToolbar}
            renderToolbarOld={renderToolbarOld}
            renderVariable={renderVariable}
            show={isFocus}
            defaultFont={defaultFont}
            customColors={customColors}
            customStyles={customStyles}
            orientation={orientation}
            elementWidth={pathOr(WIDTH_A4_WITHOUT_MARGINS, ['size', 'width'], element)}
            elementHeight={pathOr(WIDTH_A4_WITHOUT_MARGINS, ['size', 'height'], element)}
            onCreateStyle={onCreateStyle}
            readOnly={not(widgetIsEditable)}
            onDuplicateElement={handleDuplicate}
            isToolbarFixed={isToolbarFixed}
            isLateEdit={isLateEdit}
            values={values}
            updateFrameStyle={updateFrameStyle}
            isUndoPreviousEditorState={isUndoPreviousEditorState}
            setIsUndoPreviousEditorState={setIsUndoPreviousEditorState}
          />
        )}
      </div>
    </>
  );
};

export default DroppedCard;
